// tslint:disable: max-file-line-count
import classNames from "classnames";
import { FieldState, FormState, ValidationResponse } from "formstate";
import { inject, observer } from "mobx-react";
import React from "react";
import DropZone from "react-dropzone";
import { CheckboxInput } from "@fidget/common/components/form/checkboxInput";
import { EditInput } from "@fidget/common/components/form/editInput";
import { InputGroup } from "@fidget/common/components/form/inputGroup";
import { ImageComponent } from "@fidget/common/components/image/image";
import { PhoneRegionInput } from "@fidget/common/components/phoneNumberInput/phoneNumberInput";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger, noop } from "@fidget/common/utils";
import { unwrapFormState } from "@fidget/common/utils/unwrapFormState";
import { validateRequired } from "@fidget/common/utils/validatorsFormState";
import { config } from "@fidget/common/config/config";
import { staticConfig } from "@fidget/common/config/static-config";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { functionCloserToString } from "../../../functionCloserToString";
import { EditableOrgConfigFields, IOrgConfig } from "../../../models/currentUser/OrgConfig";
import { IUiStore } from "../../../models/ui/UiStore";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { withPermissionFallback } from "../../HOC/withPermissionFallback/withPermissionFallback";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import { SettingsForm } from "../settingsForm";
import { SubtitleDescription } from "../subtitleDescription";
import { WelcomeMessages } from "./welcomeMessages";
import styles from "./widgetConfigSettings.css";
import { AppPermission } from "@closerplatform/spinner-openapi";
import { LanguageOption } from "@fidget/common/components/form/languagePicker/languagePicker";
import { MainNotificationInput } from "../settingsForms/mainNotificationInput/mainNotificationInput";
import { MainNotificationForm } from "../settingsForms/mainNotificationInput/types";
import { createMainNotificationForms } from "../settingsForms/mainNotificationInput/utils";
import { IFeaturesStore } from "@fidget/common/models/features/FeaturesStore";
import { SpinnerService } from "../../../services/spinner/spinnerService";
import { getWheelhouseDownloadUrl } from "@fidget/common/utils/urlMappingHelpers";

interface PictureUploadInfo {
  file?: File;
  uploading: boolean;
  tooBigError: boolean;
  base64: string;
  deleted: boolean;
}

const resetUpload: PictureUploadInfo = {
  file: undefined,
  uploading: false,
  tooBigError: false,
  base64: "",
  deleted: false
};

interface Props {
  orgConfig: IOrgConfig;
  uiStore?: IUiStore;
  featuresStore?: IFeaturesStore;
  spinnerService: SpinnerService;
}

interface State {
  logoUpload?: PictureUploadInfo;
  coverUpload?: PictureUploadInfo;
  mainNotificationForms?: MainNotificationForm[];
  mainNotificationsEdited?: boolean;
}

type FieldNames = keyof Omit<EditableOrgConfigFields, "defaultAgentAvatarToken">;

@inject("uiStore", "featuresStore")
@observer
class WidgetConfigSettingsComponent extends React.Component<Props, State> {
  private colorErrorMessage = intl.formatMessage("settings.widgetConfig.wrongColorFormat");
  companyIdTextarea?: HTMLTextAreaElement;
  codeTextarea?: HTMLTextAreaElement;
  fields: { [F in FieldNames]: FieldState<any> };
  form: FormState<any>;

  constructor(props: Props) {
    super(props);
    this.state = {
      logoUpload: resetUpload,
      coverUpload: resetUpload
    };
    const { orgConfig: initial } = this.props;
    this.fields = {
      name: new FieldState(initial.name || "").validators(validateRequired),
      headerWelcomeMessageEn: new FieldState(initial.headerWelcomeMessageEn || ""),
      headerWelcomeMessagePl: new FieldState(initial.headerWelcomeMessagePl || ""),
      baseUrl: new FieldState(initial.baseUrl || "").validators(validateRequired),
      phoneRegion: new FieldState(initial.phoneRegion).validators(validateRequired),
      color: new FieldState(initial.color || "").validators(this.validateColor),
      callsDisabled: new FieldState(initial.callsDisabled || false),
      showAdviserLastNameEnabled: new FieldState(initial.showAdviserLastNameEnabled || false),
      showAssignedAgentAvatarEnabled: new FieldState(initial.showAssignedAgentAvatarEnabled || false)
    };
    this.form = new FormState(this.fields);
  }

  validateColor = (value: string): ValidationResponse =>
    value
      ? !/^#[A-Fa-f0-9]{3}(?:[A-Fa-f0-9]{3})?$/.test(value) && this.colorErrorMessage
      : validateRequired(value);

  loadMainNotifications = async () => {
    await this.props.spinnerService
      .getMainNotifications()
      .then((dtos) => {
        const setEdited = () => {
          this.setState({ mainNotificationsEdited: true });
        };

        const forms = createMainNotificationForms(dtos);
        forms.forEach((f) => {
          f.$.text.onUpdate(setEdited);
          f.$.enabled.onUpdate(setEdited);
        });
        this.setState({ mainNotificationForms: forms, mainNotificationsEdited: false });
      })
      .catch(() => {
        this.props.uiStore!.addGenericErrorToast();
        this.setState({ mainNotificationForms: [] });
      });
  };

  componentDidMount() {
    this.loadMainNotifications();
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.orgConfig !== this.props.orgConfig) {
      this.resetFields(nextProps.orgConfig);
    }
  }

  resetFields = (orgConfig: IOrgConfig) => {
    (Object.keys(this.fields) as FieldNames[]).forEach((key) => {
      if (key === "callsDisabled") {
        this.fields.callsDisabled.reset(orgConfig.callsDisabled || false);
      } else {
        this.fields[key].reset(orgConfig[key]);
      }
    });
  };

  onFileAdded = (pictures: File[], rejectedFiles: File[], type: "logoUpload" | "coverUpload") => {
    const picture = pictures[0];
    if (picture) {
      this.loadPicture(picture, type);
    }
    if (rejectedFiles.length) {
      const message = intl.formatMessage("settings.profile.picture.error.wrongFormat");
      this.props.uiStore!.addErrorToast(message);
    }
  };

  loadPicture = (picture: File, type: "logoUpload" | "coverUpload") => {
    const state = this.state[type];
    this.setState({ [type]: resetUpload });
    if (picture.size > staticConfig.maxPictureFileSizeBytes) {
      this.setState({ [type]: { ...this.state[type], tooBigError: true } });
      const message = intl.formatMessage(`settings.widgetConfig.upload.tooBig`);
      this.props.uiStore!.addErrorToast(message);
      return;
    }
    let reader = new FileReader();
    reader.onload = (event: any) => {
      const pictureBase64 = event.target.result;
      if (state && state.base64 === pictureBase64) {
        return;
      }
      const pictureUpload = { ...this.state[type], file: picture, base64: pictureBase64 };
      this.setState({ [type]: pictureUpload });
    };
    reader.readAsDataURL(picture);
  };

  deletePicture = (type: "logoUpload" | "coverUpload") => {
    this.setState({ [type]: resetUpload });
    this.setState({
      [type]: { file: undefined, uploading: false, tooBigError: false, base64: "", deleted: true }
    });
  };

  savePictureChanges = (type: "logoUpload" | "coverUpload", orgId: string) => {
    const { orgConfig } = this.props;
    const pictureUpload = this.state[type];
    if (pictureUpload) {
      if (pictureUpload.file) {
        this.setState({ [type]: { ...pictureUpload, uploading: true } });
        if (type === "logoUpload") {
          return orgConfig.updateLogo(pictureUpload.file).then(() => {
            this.setState({ [type]: resetUpload });
            return orgConfig.logoToken;
          });
        } else {
          return orgConfig.updateCover(pictureUpload.file).then(() => {
            this.setState({ [type]: resetUpload });
            return orgConfig.logoToken;
          });
        }
      }
      if (pictureUpload.deleted) {
        return (type === "logoUpload" ? orgConfig.deleteLogo() : orgConfig.deleteCover()).then(() =>
          this.setState({ [type]: resetUpload })
        );
      }
    }
    return;
  };

  saveMainNotifications = async () => {
    const forms = this.state.mainNotificationForms;
    if (forms) {
      const updateMainNotification = (form: MainNotificationForm) => {
        if (form.$.id.$) {
          return this.props.spinnerService.patchMainNotification(form.$.id.$, {
            enabled: form.$.enabled.$,
            text: form.$.text.$
          });
        } else {
          return this.props.spinnerService
            .createMainNotification({
              enabled: form.$.enabled.$,
              locale: form.$.locale.$,
              text: form.$.text.$
            })
            .then((dto) => {
              form.$.id.reset(dto.id);
            });
        }
      };

      try {
        await Promise.all(forms.map(updateMainNotification));
        this.setState({ mainNotificationsEdited: false });
      } catch (e) {
        logger.error({ message: "Couldn't update main notifications", error: e });
        this.props.uiStore!.addGenericErrorToast();
      }
    }
  };

  discardChanges = () => {
    this.resetFields(this.props.orgConfig);
    this.setState({
      logoUpload: resetUpload,
      coverUpload: resetUpload
    });
    if (this.state.mainNotificationsEdited) {
      this.loadMainNotifications();
    }
  };

  saveChanges = async () => {
    const { orgConfig } = this.props; // existing org config
    const orgId = orgConfig.id;
    const result = await this.form.validate();
    if (result.hasError) {
      this.props.uiStore!.addErrorToast(intl.formatMessage("settings.errorInForm"));
    } else {
      try {
        this.saveMainNotifications();
        await this.savePictureChanges("logoUpload", orgId);
        await this.savePictureChanges("coverUpload", orgId);

        const data = unwrapFormState<EditableOrgConfigFields>(result.value, true);
        // prepend protocol to URL if it does not exist
        const url = data.baseUrl;
        data.baseUrl = url && (!/^HTTPS?:\/\//i.test(url.toUpperCase()) ? `http://${url}` : url);

        await orgConfig.update(data);
        this.discardChanges();
      } catch (reason) {
        this.props.uiStore!.addErrorToast(intl.formatMessage("settings.errorInForm"));
      }
    }
  };

  copyCodeToClipboard = () => {
    if (this.codeTextarea) {
      this.codeTextarea.select();
      document.execCommand("copy");
    }
  };

  copyIdToClipboard = () => {
    if (this.companyIdTextarea) {
      this.companyIdTextarea.select();
      document.execCommand("copy");
    }
  };

  renderShowAdviserLastNameSection() {
    return (
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.widgetConfig.additionalConfig")}
        />
        <div className={commonStyles.content}>
          <CheckboxInput
            value={this.fields.showAdviserLastNameEnabled.value}
            id={"agent_last_name"}
            label={intl.formatMessage("settings.widgetConfig.additionalConfig.adviserLastName")}
            onChange={this.fields.showAdviserLastNameEnabled.onChange}
          />
          <CheckboxInput
            value={this.fields.showAssignedAgentAvatarEnabled.value}
            id={"minimize_avatar"}
            label={intl.formatMessage("settings.widgetConfig.additionalConfig.minimizeAvatar")}
            onChange={this.fields.showAssignedAgentAvatarEnabled.onChange}
          />
        </div>
      </div>
    );
  }

  render() {
    const { form, fields } = this;
    const { orgConfig } = this.props;
    const { logoUpload, coverUpload } = this.state;
    const orgId = orgConfig.id;
    const logoSrc = logoUpload && !logoUpload.deleted && (logoUpload.base64 ||
      (orgConfig.logoToken && getWheelhouseDownloadUrl(orgConfig.logoToken)));
    const coverSrc = coverUpload && !coverUpload.deleted && (coverUpload.base64 ||
      (orgConfig.coverToken && getWheelhouseDownloadUrl(orgConfig.coverToken)));
    const dropColor = fields.color.hasError ? "var(--colorBase30)" : fields.color.value;
    const baseWidgetURL = config.services.spinner.hostname.replace("spinner.", "");
    const widgetProtocol = config.services.spinner.protocol;
    const companyID = orgId;
    const closerFunction = orgId && functionCloserToString(orgId, baseWidgetURL, widgetProtocol);
    const isChanged =
      Object.values(fields).some((f) => !!f.dirty) ||
      (logoUpload && (logoUpload.base64 || logoUpload.deleted)) ||
      (coverUpload && (coverUpload.base64 || coverUpload.deleted)) ||
      !!this.state.mainNotificationsEdited;
    return (
      <SettingsForm
        onDiscard={this.discardChanges}
        disabled={!isChanged}
        description={intl.formatMessage("settings.widgetConfig.fewMinutes")}
        onSubmit={this.saveChanges}
      >
        <div className={commonStyles.title}>
          <L10n id={"settings.widgetConfig.branding"} />
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.widgetConfig.companyName")}
            description={intl.formatMessage("settings.widgetConfig.companyName.description")}
          />
          <div className={commonStyles.content}>
            <InputGroup>
              <EditInput
                value={fields.name.value}
                onChange={(ev) => fields.name.onChange(ev.currentTarget.value)}
                className={commonStyles.input}
                hasError={fields.name.hasError}
                errorHint={fields.name.error}
                placeholder={intl.formatMessage("settings.widgetConfig.companyName.placeholder")}
                maxLength={50}
                autoComplete={"organization"}
              />
            </InputGroup>
          </div>
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.widgetConfig.companyLogo")}
            description={intl.formatMessage("settings.widgetConfig.companyLogo.description")}
          />
          <div className={commonStyles.content}>
            <DropZone
              className={classNames(styles["picture-section"], styles["logo-section"], {
                [styles.loading]: this.state.logoUpload && this.state.logoUpload.uploading
              })}
              activeClassName={styles.active}
              onDrop={(pictures, rejectedFiles) =>
                this.onFileAdded(pictures, rejectedFiles, "logoUpload")
              }
              accept={"image/jpeg, image/png"}
            >
              <div className={classNames(styles.picture, styles.logo)}>
                {logoSrc && <ImageComponent src={logoSrc} />}
                {this.state.logoUpload && this.state.logoUpload.uploading && (
                  <div className={styles.loader}>
                    <CloserLoaderComponent />
                  </div>
                )}
              </div>
              <div className={classNames(styles["picture-info"])}>
                <div className={styles.title}>
                  <L10n id={"settings.widgetConfig.companyLogo.upload"} />
                </div>
                <div
                  className={classNames(styles.description, {
                    [styles.error]: this.state.logoUpload && this.state.logoUpload.tooBigError
                  })}
                >
                  <L10n id={"settings.widgetConfig.companyLogo.upload.description"} />
                </div>
              </div>
              <div className={styles["dragdrop-info"]}>
                <div className={styles["bordered-container"]}>
                  <div className={styles.text}>
                    <i className={classNames("icon-upload", styles["upload-icon"])} />
                    <L10n id={"settings.profile.picture.drag"} />
                  </div>
                </div>
              </div>
            </DropZone>
            <div className={styles.title} onClick={() => this.deletePicture("logoUpload")}>
              <L10n id={"settings.profile.picture.remove"} />
            </div>
          </div>
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.widgetConfig.companyBrandColour")}
            description={intl.formatMessage("settings.widgetConfig.companyBrandColour.description")}
          />
          <div className={commonStyles.content}>
            <InputGroup>
              <div className={styles["color-drop"]} style={{ backgroundColor: dropColor }} />
              <EditInput
                value={fields.color.value}
                onChange={(ev) => fields.color.onChange((ev.target as HTMLInputElement).value)}
                hasError={fields.color.hasError}
                errorHint={fields.color.error}
                className={classNames(commonStyles.input, styles["color-input"])}
                placeholder={intl.formatMessage(
                  "settings.widgetConfig.companyBrandColour.placeholder"
                )}
                maxLength={7}
              />
            </InputGroup>
          </div>
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.widgetConfig.companyCover")}
            description={intl.formatMessage("settings.widgetConfig.companyCover.description")}
          />
          <div className={commonStyles.content}>
            <DropZone
              className={classNames(styles["picture-section"], styles["cover-section"], {
                [styles.loading]: this.state.coverUpload && this.state.coverUpload.uploading
              })}
              activeClassName={styles.active}
              onDrop={(pictures, rejectedFiles) =>
                this.onFileAdded(pictures, rejectedFiles, "coverUpload")
              }
              accept={"image/jpeg, image/png"}
            >
              <div className={styles["cover-container"]}>
                <div className={classNames(styles.picture, styles.cover)}>
                  {coverSrc && <ImageComponent src={coverSrc} />}
                  {this.state.coverUpload && this.state.coverUpload.uploading && (
                    <div className={styles.loader}>
                      <CloserLoaderComponent />
                    </div>
                  )}
                </div>
                <div className={styles.line}>
                  <div className={styles["high-info"]}>
                    <L10n id={"settings.widgetConfig.companyCover.headerHigher"} />
                  </div>
                </div>
              </div>
              <div className={classNames(styles["picture-info"])}>
                <div className={styles.title}>
                  <L10n id={"settings.widgetConfig.companyCover.upload"} />
                </div>
                <div
                  className={classNames(styles.description, {
                    [styles.error]: this.state.coverUpload && this.state.coverUpload.tooBigError
                  })}
                >
                  <L10n id={"settings.widgetConfig.companyCover.upload.description"} />
                </div>
              </div>
              <div className={styles["dragdrop-info"]}>
                <div className={styles["bordered-container"]}>
                  <div className={styles.text}>
                    <i className={classNames("icon-upload", styles["upload-icon"])} />
                    <L10n id={"settings.profile.picture.drag"} />
                  </div>
                </div>
              </div>
            </DropZone>
            <div className={styles.title} onClick={() => this.deletePicture("coverUpload")}>
              <L10n id={"settings.profile.picture.remove"} />
            </div>
          </div>
        </div>
        {this.renderShowAdviserLastNameSection()}
        <div className={commonStyles.title}>
          <L10n id={"settings.widgetConfig.messages"} />
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.widgetConfig.headersWelcomeMessage")}
            description={intl.formatMessage(
              "settings.widgetConfig.headersWelcomeMessage.description",
              {
                message: intl.formatMessage("widget.chat.welcomeMessage", {
                  companyName: this.props.orgConfig.name
                })
              }
            )}
          />
          <div className={commonStyles.content}>
            <WelcomeMessages
              languagesProps={[
                {
                  language: LanguageOption.Polish,
                  value: fields.headerWelcomeMessagePl.value,
                  onChange: fields.headerWelcomeMessagePl.onChange,
                  placeholder: intl.formatMessage(
                    "settings.widgetConfig.headersWelcomeMessage.placeholder"
                  )
                },
                {
                  language: LanguageOption.English,
                  value: fields.headerWelcomeMessageEn.value,
                  onChange: fields.headerWelcomeMessageEn.onChange,
                  placeholder: intl.formatMessage(
                    "settings.widgetConfig.headersWelcomeMessage.placeholder"
                  )
                }
              ]}
              maxLength={50}
            />
          </div>
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.mainNotifications.subtitle")}
            description={intl.formatMessage("settings.mainNotifications.description", {
              maxCharacters: staticConfig.mainNotificationMaxCharacters
            })}
          />
          <div className={commonStyles.content}>
            {this.state.mainNotificationForms ? (
              <MainNotificationInput fields={this.state.mainNotificationForms} />
            ) : (
              <CloserLoaderComponent />
            )}
          </div>
        </div>
        <div className={commonStyles.title}>
          <L10n id={"settings.widgetConfig.defaultPhonePrefix"} />
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            description={intl.formatMessage("settings.widgetConfig.defaultPhonePrefix.description")}
          />
          <div className={commonStyles.content}>
            <PhoneRegionInput
              field={fields.phoneRegion}
              placeholder={intl.formatMessage(
                "settings.widgetConfig.defaultPhonePrefix.placeholder"
              )}
              emptyPlaceholder={intl.formatMessage("profile.edit.noMatchingCountries")}
              className={classNames(commonStyles.input, styles["prefix-input"])}
              menuClassName={styles["prefix-input"]}
            />
          </div>
        </div>
        <div className={commonStyles.title}>
          <L10n id={"settings.widgetConfig.agentsContactOptions"} />
        </div>
        <div className={commonStyles.section}>
          <div className={commonStyles.content}>
            <CheckboxInput
              value={fields.callsDisabled.value}
              id={"agents_contact_options"}
              label={intl.formatMessage(
                "settings.widgetConfig.agentsContactOptions.agentsChatOnly"
              )}
              onChange={fields.callsDisabled.onChange}
            />
          </div>
        </div>
        <div className={commonStyles.title}>
          <L10n id={"settings.widgetConfig.websiteDomain"} />
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription
            description={intl.formatMessage("settings.widgetConfig.websiteDomain.description")}
          />
          <div className={commonStyles.content}>
            <InputGroup>
              <EditInput
                value={fields.baseUrl.value}
                onChange={(ev) => fields.baseUrl.onChange(ev.currentTarget.value)}
                className={commonStyles.input}
                placeholder={intl.formatMessage("settings.widgetConfig.websiteDomain.placeholder")}
                autoComplete={"url"}
                hasError={fields.baseUrl.hasError}
                errorHint={fields.baseUrl.error}
                type={"url"}
              />
            </InputGroup>
          </div>
        </div>
        {!form.hasError && (
          <>
            <div className={commonStyles.title}>
              <L10n id={"settings.widgetConfig.wordpressPlugin"} />
            </div>
            <div className={commonStyles.section}>
              <SubtitleDescription
                description={intl.formatMessage(
                  "settings.widgetConfig.wordpressPlugin.description"
                )}
              />
              <div className={commonStyles.content}>
                <div className={styles.code}>
                  <textarea
                    value={companyID}
                    onChange={noop}
                    ref={(e) => (this.companyIdTextarea = e || undefined)}
                    rows={1}
                  />
                </div>
                <AgentButton onClick={this.copyIdToClipboard}>
                  <L10n id={"settings.widgetConfig.wordpressPlugin.copy"} />
                </AgentButton>
              </div>
            </div>
            <div className={commonStyles.title}>
              <L10n id={"settings.widgetConfig.websiteIntegration"} />
            </div>
            <div className={commonStyles.section}>
              <SubtitleDescription
                description={intl.formatMessage(
                  "settings.widgetConfig.websiteIntegration.description"
                )}
              />
              <div className={commonStyles.content}>
                <div className={styles.code}>
                  <textarea
                    value={closerFunction}
                    onChange={noop}
                    ref={(e) => (this.codeTextarea = e || undefined)}
                    rows={21}
                  />
                </div>
                <AgentButton onClick={this.copyCodeToClipboard}>
                  <L10n id={"settings.widgetConfig.websiteIntegration.copy"} />
                </AgentButton>
              </div>
            </div>
          </>
        )}
        <div className={commonStyles.section}>
          <div className={commonStyles["subtitle-description"]}>
            <div className={commonStyles.description}>
              <span className={commonStyles.subtitle}>*</span>
              <L10nHtml id={"settings.widgetConfig.obligatory"} />
            </div>
          </div>
        </div>
      </SettingsForm>
    );
  }
}

export const WidgetConfigSettings = withPermissionFallback(
  AppPermission.SettingsGeneral,
  WidgetConfigSettingsComponent
);
